/**
 * Step 1 — Hardware Setup (Phase 4 update).
 * - Management-port dropdown now reads ports DIRECTLY from `hardwareData` →
 *   no race with `activeHardwareModel` hydration.
 * - Filter relaxed: excludes only `wan` and `console`.
 * - Default mgmt port marked with a "★ DEFAULT" tag in the option label.
 * - Pre-selects the default mgmt port when `mgmt_port` is empty.
 * - Honors `readOnly` (Demo mode for free users).
 */
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Router, ShieldCheck, Cloud, Server, Cpu,
    Check, AlertCircle, Eye, EyeOff,
} from "lucide-react";
import { useWizardStore } from "../state/wizardStore";
import { TIMEZONE_MAP, validateIPv4, formatIPInput } from "../utils/validation";

const VENDORS = [
    { code: "fortinet",   label: "Fortinet",     accent: "#D72B2B", subtitle: "FortiOS — FortiGate 30G to 200F + VM", icon: ShieldCheck },
    { code: "zyxel",      label: "Zyxel ZLD",    accent: "#03D8F3", subtitle: "USG FLEX 50–700, ATP100–800",          icon: Router },
    { code: "zyxel_uos",  label: "Zyxel uOS",    accent: "#03D8F3", subtitle: "USG LITE 60AX, FLEX H series (Nebula)", icon: Cloud, badge: "uOS" },
    { code: "watchguard", label: "WatchGuard",   accent: "#03D8F3", subtitle: "Firebox T25 → M590",                    icon: Server },
    { code: "pfsense",    label: "pfSense",      accent: "#03D8F3", subtitle: "Netgate 1100 → 6100, VM, custom builds", icon: Cpu },
];

const TIMEZONE_ENTRIES = Object.entries(TIMEZONE_MAP);

const isValidHostname = (h) => /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$|^[a-z0-9]$/.test(h);
const isValidPassword = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);
const pwStrength = (p) => {
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return Math.min(s, 4);
};

const VendorCard = ({ vendor, isSelected, onSelect, disabled }) => {
    const Icon = vendor.icon;
    return (
        <button
            type="button"
            onClick={() => !disabled && onSelect(vendor.code)}
            disabled={disabled}
            data-testid={`vendor-card-${vendor.code}`}
            className={[
                "group relative p-5 rounded-xl border bg-surface/70 backdrop-blur-md text-left transition-all duration-300",
                isSelected
                    ? "border-accent-cyan shadow-glow-cyan"
                    : "border-white/[0.06] hover:border-accent-cyan/30 hover:scale-[1.02]",
                disabled && "opacity-60 cursor-not-allowed hover:scale-100",
            ].filter(Boolean).join(" ")}
        >
            <span aria-hidden className="absolute top-0 left-5 right-5 h-[3px] rounded-b-full" style={{ background: vendor.accent }} />
            <div className="flex items-start justify-between mb-4">
                <span
                    className={[
                        "inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors",
                        isSelected ? "bg-accent-cyan/15 border-accent-cyan/40 text-accent-cyan" : "bg-white/[0.03] border-white/[0.06] text-text-muted",
                    ].join(" ")}
                >
                    <Icon className="w-4 h-4" />
                </span>
                {vendor.badge && (
                    <span className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-accent-cyan border border-accent-cyan/30 px-1.5 py-0.5 rounded">
                        {vendor.badge}
                    </span>
                )}
                {isSelected && <Check className="w-4 h-4 text-accent-cyan" />}
            </div>
            <p className="text-[14.5px] font-semibold text-text-primary">{vendor.label}</p>
            <p className="text-[11.5px] text-text-muted leading-relaxed mt-1.5">{vendor.subtitle}</p>
        </button>
    );
};

const ModelCard = ({ model, isSelected, onSelect, disabled }) => {
    const summary = useMemo(() => {
        const wan = model.ports.filter((p) => p.type === "wan").length;
        const lan = model.ports.filter((p) => p.type === "lan").length;
        const sfp = model.ports.filter((p) => p.type === "sfp" || p.type === "combo").length;
        const parts = [];
        if (lan) parts.push(`${lan}× LAN`);
        if (wan) parts.push(`${wan}× WAN`);
        if (sfp) parts.push(`${sfp}× SFP`);
        return parts.join(" · ");
    }, [model]);
    return (
        <button
            type="button"
            onClick={() => !disabled && onSelect(model.name)}
            disabled={disabled}
            data-testid={`model-card-${model.id}`}
            className={[
                "group p-4 rounded-lg border bg-surface/70 text-left transition-all duration-200",
                isSelected ? "border-accent-cyan shadow-glow-cyan" : "border-white/[0.06] hover:border-accent-cyan/30 hover:scale-[1.02]",
                disabled && "opacity-60 cursor-not-allowed hover:scale-100",
            ].filter(Boolean).join(" ")}
        >
            <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-text-primary">{model.displayName}</p>
                {isSelected && <Check className="w-3.5 h-3.5 text-accent-cyan" />}
            </div>
            <p className="text-[10.5px] font-mono text-text-muted mt-1">{summary || `${model.ports.length} ports`}</p>
            {model.notes && (
                <p className="text-[10px] text-text-dim mt-2 line-clamp-2 leading-snug">{model.notes}</p>
            )}
        </button>
    );
};

const ConfigForm = ({ readOnly, currentModelObj }) => {
    const {
        vendor, hostname, password_admin, dns_primary, dns_secondary,
        timezone_id, mgmt_port, mgmt_ip, mgmt_subnet,
        pfsense_interface_prefix, pfsense_port_count, pfsense_custom_ports,
        updateStep1, setPfSenseInterfacePrefix, setPfSensePortCount, generatePfSensePorts,
    } = useWizardStore();

    const [showPw, setShowPw] = useState(false);
    const [tzQuery, setTzQuery] = useState("");
    const [tzOpen, setTzOpen] = useState(false);

    const tzLabel = useMemo(
        () => TIMEZONE_ENTRIES.find(([, code]) => code === timezone_id)?.[0] || "Europe/Rome",
        [timezone_id],
    );
    const tzMatches = useMemo(() => {
        const q = tzQuery.toLowerCase();
        return TIMEZONE_ENTRIES.filter(([name]) => name.toLowerCase().includes(q)).slice(0, 12);
    }, [tzQuery]);

    // Phase 4 mgmt-port fix: read ports directly from the passed-in model object
    // (sourced from hardwareData), not from `activeHardwareModel` in the store.
    // Filter excludes only `wan` and `console`.
    const availablePorts = useMemo(() => {
        if (!currentModelObj) return [];
        return (currentModelObj.ports || []).filter(
            (p) => p.type !== "wan" && p.type !== "console",
        );
    }, [currentModelObj]);

    const defaultMgmtPort = currentModelObj?.defaultMgmtPort || currentModelObj?.defaultLanPort || availablePorts[0]?.name;
    const showMgmtIp = !!currentModelObj?.ports?.some((p) => p.type === "mgmt");
    const isPfSense = vendor === "pfsense";

    const update = (data) => !readOnly && updateStep1(data);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-xl border border-white/[0.06] bg-surface/60 backdrop-blur-md p-6 space-y-6"
            data-testid="hardware-config-form"
        >
            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Configuration</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Hostname</label>
                    <input
                        type="text"
                        value={hostname}
                        readOnly={readOnly}
                        onChange={(e) => update({ hostname: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 32) })}
                        placeholder="fw-hq-01"
                        data-testid="hardware-hostname"
                        className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan transition-colors disabled:opacity-60"
                    />
                    {hostname && !isValidHostname(hostname) && (
                        <p className="mt-1.5 text-[10.5px] text-accent-amber font-mono">
                            <AlertCircle className="inline w-3 h-3 mr-1" />
                            Use lowercase letters, digits, and hyphens (2–32 chars).
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Admin password</label>
                    <div className="relative">
                        <input
                            type={showPw ? "text" : "password"}
                            value={password_admin}
                            readOnly={readOnly}
                            onChange={(e) => update({ password_admin: e.target.value })}
                            placeholder="Min 8 chars · letters + digits"
                            data-testid="hardware-password"
                            className="w-full px-3.5 py-2.5 pr-10 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan transition-colors"
                        />
                        <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-dim hover:text-text-primary" tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"}>
                            {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                    {password_admin && (
                        <div className="mt-2 flex gap-1">
                            {[0, 1, 2, 3].map((i) => (
                                <span key={i} className={["h-1 flex-1 rounded-full transition-colors",
                                    i < pwStrength(password_admin)
                                        ? pwStrength(password_admin) >= 3 ? "bg-accent-mint" : pwStrength(password_admin) >= 2 ? "bg-accent-cyan" : "bg-accent-amber"
                                        : "bg-white/[0.06]",
                                ].join(" ")} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">DNS primary</label>
                    <input type="text" value={dns_primary} readOnly={readOnly} onChange={(e) => update({ dns_primary: formatIPInput(e.target.value) })} placeholder="8.8.8.8" data-testid="hardware-dns-primary" className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan" />
                </div>
                <div>
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">DNS secondary</label>
                    <input type="text" value={dns_secondary} readOnly={readOnly} onChange={(e) => update({ dns_secondary: formatIPInput(e.target.value) })} placeholder="1.1.1.1" data-testid="hardware-dns-secondary" className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono placeholder:text-text-dim focus:outline-none focus:border-accent-cyan focus:shadow-glow-cyan" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative">
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Timezone</label>
                    <button type="button" disabled={readOnly} onClick={() => setTzOpen((v) => !v)} data-testid="hardware-timezone-trigger" className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary text-left flex items-center justify-between hover:border-accent-cyan/30 focus:outline-none focus:border-accent-cyan transition-colors disabled:opacity-60">
                        <span className="font-mono">{tzLabel}</span>
                        <span className="text-[10.5px] font-mono text-text-dim">code {timezone_id}</span>
                    </button>
                    {tzOpen && !readOnly && (
                        <div className="absolute z-20 mt-1 w-full bg-surface border border-white/[0.08] rounded-md shadow-heavy-popover overflow-hidden">
                            <input autoFocus value={tzQuery} onChange={(e) => setTzQuery(e.target.value)} placeholder="Search…" className="w-full px-3.5 py-2.5 bg-canvas border-b border-white/[0.05] text-[13px] text-text-primary placeholder:text-text-dim focus:outline-none" />
                            <div className="max-h-64 overflow-y-auto">
                                {tzMatches.length === 0 && <div className="px-3.5 py-3 text-[12px] text-text-dim">No match</div>}
                                {tzMatches.map(([name, code]) => (
                                    <button key={code} type="button" onClick={() => { update({ timezone_id: code }); setTzOpen(false); setTzQuery(""); }} data-testid={`tz-option-${code}`} className="w-full px-3.5 py-2 text-left text-[13px] hover:bg-accent-cyan/10 transition-colors flex items-center justify-between">
                                        <span className="text-text-primary">{name.replace(/_/g, " ")}</span>
                                        <span className="text-[10.5px] font-mono text-text-dim">{code}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Management port</label>
                    <select
                        value={mgmt_port || ""}
                        disabled={readOnly}
                        onChange={(e) => update({ mgmt_port: e.target.value })}
                        data-testid="hardware-mgmt-port"
                        className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan transition-colors disabled:opacity-60"
                    >
                        <option value="">Select port…</option>
                        {availablePorts.map((p) => {
                            const isDefault = p.name === defaultMgmtPort;
                            const label = `${p.name} · ${p.speed}${p.type === "mgmt" ? " (dedicated mgmt)" : ""}${isDefault ? "  ★ DEFAULT" : ""}`;
                            return <option key={p.name} value={p.name}>{label}</option>;
                        })}
                    </select>
                    {availablePorts.length === 0 && (
                        <p className="mt-1.5 text-[10.5px] text-accent-amber font-mono">
                            <AlertCircle className="inline w-3 h-3 mr-1" /> No ports available — re-select the model.
                        </p>
                    )}
                </div>
            </div>

            {showMgmtIp && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-white/[0.04]">
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Dedicated mgmt IP</label>
                        <input type="text" value={mgmt_ip} readOnly={readOnly} onChange={(e) => update({ mgmt_ip: formatIPInput(e.target.value) })} data-testid="hardware-mgmt-ip" className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-text-muted mb-1.5">Mgmt subnet mask</label>
                        <input type="text" value={mgmt_subnet} readOnly={readOnly} onChange={(e) => update({ mgmt_subnet: formatIPInput(e.target.value) })} data-testid="hardware-mgmt-subnet" className="w-full px-3.5 py-2.5 bg-canvas border border-white/[0.08] rounded-md text-[14px] text-text-primary font-mono focus:outline-none focus:border-accent-cyan" />
                    </div>
                </div>
            )}

            {isPfSense && (
                <div className="rounded-lg border border-accent-cyan/20 bg-accent-cyan/[0.04] p-4 space-y-3">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-accent-cyan">pfSense custom interfaces (VM / DIY builds)</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[10.5px] text-text-muted mb-1">Driver prefix</label>
                            <select disabled={readOnly} value={pfsense_interface_prefix} onChange={(e) => { setPfSenseInterfacePrefix(e.target.value); generatePfSensePorts(); }} className="w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded text-[13px] font-mono disabled:opacity-60">
                                {["igb", "em", "ix", "vtnet", "vmx", "hn"].map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10.5px] text-text-muted mb-1">Port count</label>
                            <input type="number" disabled={readOnly} min={2} max={16} value={pfsense_port_count} onChange={(e) => { setPfSensePortCount(Number(e.target.value)); generatePfSensePorts(); }} className="w-full px-3 py-2 bg-canvas border border-white/[0.08] rounded text-[13px] font-mono disabled:opacity-60" />
                        </div>
                    </div>
                    {pfsense_custom_ports?.length > 0 && <p className="text-[10.5px] font-mono text-text-muted">Generated: {pfsense_custom_ports.join(", ")}</p>}
                </div>
            )}
        </motion.div>
    );
};

export const Step1HardwareSetup = ({ hardwareData, readOnly = false }) => {
    const { vendor, modello, setVendor, updateStep1 } = useWizardStore();

    const vendors = hardwareData?.vendors || [];
    const currentVendorBlock = useMemo(
        () => vendors.find((v) => v.vendorCode === vendor),
        [vendors, vendor],
    );
    const models = currentVendorBlock?.models || [];
    const currentModelObj = useMemo(
        () => models.find((m) => m.name === modello) || null,
        [models, modello],
    );

    return (
        <div className="space-y-10" data-testid="step1-hardware-setup">
            <section>
                <header className="mb-5">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Step 1 of 7</p>
                    <h2 className="mt-2 font-display font-semibold text-3xl tracking-tight">Choose your hardware</h2>
                    <p className="mt-2 text-[13.5px] text-text-muted">Pick a vendor, then a model. Everything downstream — port layouts, factory cleanup, CLI dialect — flows from this choice.</p>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                    {VENDORS.map((v) => (
                        <VendorCard key={v.code} vendor={v} isSelected={vendor === v.code} onSelect={(code) => setVendor(code)} disabled={readOnly} />
                    ))}
                </div>
            </section>

            <AnimatePresence>
                {currentVendorBlock && (
                    <motion.section key="models" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                        <header className="mb-4">
                            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">{currentVendorBlock.vendor}</p>
                            <h3 className="mt-1.5 font-display font-semibold text-xl">Select model</h3>
                        </header>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="model-grid">
                            {models.map((m) => (
                                <ModelCard key={m.id} model={m} isSelected={modello === m.name} onSelect={(name) => updateStep1({ modello: name })} disabled={readOnly} />
                            ))}
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {modello && currentModelObj && <ConfigForm key="form" readOnly={readOnly} currentModelObj={currentModelObj} />}
            </AnimatePresence>
        </div>
    );
};

export function isStep1Valid(state) {
    return (
        !!state.vendor && !!state.modello &&
        isValidHostname(state.hostname || "") &&
        isValidPassword(state.password_admin || "") &&
        validateIPv4(state.dns_primary || "") &&
        validateIPv4(state.dns_secondary || "") &&
        !!state.timezone_id && !!state.mgmt_port
    );
}

export default Step1HardwareSetup;
